import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, ElementRef, input, OnDestroy, OnInit, output, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MenuItem } from 'primeng/api';
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { PopoverModule, Popover } from 'primeng/popover';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule, FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NavItem } from '../../../models/navigation.model';
import { Router } from '@angular/router';
import { StringUtils } from 'src/app/utility/stringUtils';
import { IconService } from 'src/app/ui/icon.service';
import { SearchService, SearchResult } from 'src/app/api/search.service';
import { TableModule } from 'primeng/table';
import { DataPadService } from 'src/app/ui/data-pad/data-pad.service';
import { EditNoteComponentInputs } from 'src/app/component-modules/notes/edit-note/edit-note.component';
import { EditScheduledActivityComponentInputs } from 'src/app/component-modules/notes/edit-scheduled-activity/edit-scheduled-activity.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Subject, debounceTime, distinctUntilChanged, switchMap, catchError, of, map } from 'rxjs';
import { Column } from 'src/app/ui/tables';
import { TableHeadersComponent } from 'src/app/ui/tables';
import { TableColumnsComponent } from 'src/app/ui/tables';
import { IconButtonComponent } from 'src/app/ui/icon-button/icon-button.component';
import { IconField } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TooltipModule } from 'primeng/tooltip';
import { TripService } from 'src/app/api/trip.service';




@Component({
  selector: 'app-nav-search',
  imports: [CommonModule, AutoCompleteModule, PopoverModule, InputTextModule, ButtonModule, RippleModule, FormsModule, FaIconComponent, TableModule, ProgressSpinnerModule, TableHeadersComponent, TableColumnsComponent, IconButtonComponent, IconField, InputIconModule, TooltipModule],
  templateUrl: './nav-search.component.html',
  styleUrl: './nav-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavSearchComponent implements OnInit, OnDestroy {
  ngOnInit(): void {

  }

  ngOnDestroy(): void {
    this.removeGlobalClickListener();
    this.removeBackdrop();
  }

  navItems = input.required<NavItem[]>()
  closeOtherMenus = output<void>();
  isMobileMode = input<boolean>(false);

  suggestions = signal<{ label: string, routerLink: string | undefined }[]>([]);

  filteredSuggestions = signal<{ label: string, routerLink: string | undefined }[]>([]);

  searchQuery = signal<string>('');
  searchValue: string = '';
  isSearchActive: boolean = false;

  // Search results and loading state
  searchResults = signal<SearchResult[]>([]);
  isLoading = signal<boolean>(false);
  searchError = signal<string | null>(null);
  hasSearched = signal<boolean>(false);
  hasPerformedSearch = signal<boolean>(false); // Track if we've actually performed a search (not just cleared)
  totalResultCount = signal<number>(0);
  
  // Smart rendering strategy
  useVirtualScrolling = computed(() => this.totalResultCount() > 1000);
  useTableScrolling = computed(() => this.totalResultCount() > 10);

  // Search subject for debounced search
  private searchSubject = new Subject<string>();

  // Table columns for search results (matching ColdFusion exact order)
  searchColumns = signal<Column<SearchResult>[]>([
    {
      field: 'type',
      header: 'Type',
      type: 'text',
      sortable: true,
      columnClass: 'text-center',
      headerClass: 'text-center'
    },
    {
      field: 'groupId',
      header: 'Group ID',
      type: 'groupNameWithLink',
      custIdField: 'groupId',
      sortable: true,
      columnClass: 'text-center',
      headerClass: 'text-center'
    },
    {
      field: 'name',
      header: 'Name',
      type: 'groupNameWithLink',
      custIdField: 'groupId',
      sortable: true,
      columnClass: 'font-weight-bold'
    },
    {
      field: 'company',
      header: 'Company',
      type: 'groupNameWithLink',
      custIdField: 'groupId',
      sortable: true
    },
    {
      field: 'phone1',
      header: 'Phone 1',
      type: 'phone',
      sortable: true
    },
    {
      field: 'phone2',
      header: 'Phone 2',
      type: 'phone',
      sortable: true
    },
    {
      field: 'email',
      header: 'Email',
      type: 'email',
      sortable: true
    }
  ]);

  @ViewChild('autocomplete') autocomplete!: AutoComplete;
  @ViewChild('searchPopover') searchPopover!: Popover;
  @ViewChild('searchResultsTable') searchResultsTable: any;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('searchInputMobile') searchInputMobile!: ElementRef<HTMLInputElement>;

  constructor(
    private router: Router,
    public icons: IconService,
    private searchService: SearchService,
    private cdr: ChangeDetectorRef,
    private dataPadService: DataPadService,
    private tripService: TripService
  ) {
    effect(() => {
      const navItems = this.navItems();
      const suggestions = this.buildNavItemSuggestions(navItems);
      this.suggestions.set(suggestions);
      this.filteredSuggestions.set(suggestions);

    });

    // Set up debounced search
    this.searchSubject.pipe(
      debounceTime(300), // Wait 300ms after user stops typing
      // Removed distinctUntilChanged to allow searching the same term multiple times
      switchMap(searchTerm => {
        if (!searchTerm || searchTerm.trim().length === 0) {
          this.searchResults.set([]);
          this.isLoading.set(false);
          this.searchError.set(null);
          this.totalResultCount.set(0);
          return of([]);
        }
        
        this.isLoading.set(true);
        this.searchError.set(null);
        this.hasPerformedSearch.set(true); // Mark that we've performed a search

        // Load all results at once
        return this.searchService.search(searchTerm, true).pipe(
          map(results => {
            this.searchResults.set(results);
            this.totalResultCount.set(results.length);
            this.isLoading.set(false);
            return results;
          }),
          catchError(error => {
            console.error('Search error:', error);
            this.searchError.set('Search failed. Please try again.');
            this.isLoading.set(false);
            this.searchResults.set([]);
            this.totalResultCount.set(0);
            return of([]);
          })
        );
      })
    ).subscribe({
      next: (results) => {
        // Force change detection to ensure the table updates
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Search subscription error:', error);
        this.isLoading.set(false);
        this.searchError.set('Search failed. Please try again.');
        this.searchResults.set([]);
        this.totalResultCount.set(0);
        this.cdr.detectChanges();
      }
    });
  }

  buildNavItemSuggestions(navItems: NavItem[] | MenuItem[], parentPath: string = '') {

    let suggestions: { label: string, routerLink: string | undefined }[] = []

    for (const navItem of navItems) {

      if (navItem.hasSubItems && (navItem.subItems || navItem.menuItems)) {
        // If the nav item has sub-items, we need to flatten them
        suggestions = suggestions.concat(this.buildNavItemSuggestions(navItem.subItems || navItem.menuItems, parentPath + StringUtils.titleCase(navItem.label ?? '') + '/'));
      }
      else {
        // If the nav item does not have sub-items, we can add it directly
        suggestions.push({
          label: parentPath + StringUtils.titleCase(navItem.label ?? ''),

          routerLink: navItem.routerLink,

        });
      }

    }


    return suggestions
  }

  search(event: AutoCompleteCompleteEvent) {
    const query = event.query.toLowerCase();
    const filteredSuggestions = this.suggestions().filter(item => item.label.toLowerCase().includes(query));

    this.filteredSuggestions.set(filteredSuggestions);

  }

  selected(event: AutoCompleteSelectEvent) {
    if (event.value.routerLink) {
      this.router.navigate([event.value.routerLink]);
      if (this.autocomplete) {
        this.autocomplete.clear();
      }
    }
  }

  focus() {
    
      if (this.isMobileMode()) {
        if (this.searchInputMobile) {
          this.searchInputMobile.nativeElement.focus();
        }
      } else {
        if (this.searchInput) {


          this.searchInput.nativeElement.focus();
        }
      }

    
    if (this.autocomplete && this.autocomplete.inputEL) {
      this.autocomplete.inputEL.nativeElement.focus();
    }
  }

  toggleSearch(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    // Emit event to parent to close all other menus and clear active nav items
    this.closeOtherMenus.emit();

    // Set active state to true when opening search
    this.isSearchActive = true;

    // Create backdrop element and append to body
    this.createBackdrop();

    // Toggle the popover
    this.searchPopover.toggle(event);
  }

  onSearchPopoverHide() {
    // Clear active state when popover is hidden
    this.isSearchActive = false;
    // Remove backdrop
    this.removeBackdrop();
    // Remove global click listener
    this.removeGlobalClickListener();
  }

  onSearchPopoverShow() {
    this.focus();
    // Add a global click listener to handle PrimeNG table events
    this.addGlobalClickListener();
  }

  private createBackdrop() {
    // Remove any existing backdrop first
    this.removeBackdrop();

    const backdrop = document.createElement('div');
    backdrop.className = 'search-popover-backdrop';
    backdrop.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      height: 100dvh; /* Use dynamic viewport height for mobile */
      background-color: rgba(0, 0, 0, 0.3);
      backdrop-filter: blur(2px);
      z-index: 1000;
      cursor: pointer;
    `;
    // Remove backdrop click handler - let the global click listener handle all clicks
    // This prevents conflicts between backdrop and global click handlers

    document.body.appendChild(backdrop);
  }

  private removeBackdrop() {
    const existingBackdrop = document.querySelector('.search-popover-backdrop');
    if (existingBackdrop) {
      existingBackdrop.remove();
    }
  }

  private globalClickListener: ((event: Event) => void) | null = null;

  private addGlobalClickListener() {
    this.removeGlobalClickListener(); // Remove any existing listener first
    
    this.globalClickListener = (event: Event) => {
      const target = event.target as HTMLElement;
      
      // Check if the click is inside the popover
      const popoverElement = document.querySelector('.centered-search-popover');
      if (popoverElement && popoverElement.contains(target)) {
        // Don't close if clicking inside the popover
        // event.stopPropagation();
        return;
      }
      
      // Check if the click is on the search button (to allow toggling)
      const searchButton = document.querySelector('.search-icon-btn');
      if (searchButton && searchButton.contains(target)) {
        return; // Allow the button click to proceed
      }
      
      // If clicking outside the popover and not on the search button, close the popover
      if (this.isSearchActive) {
        this.closeSearchPopover();
      }
    };
    
    // Add the listener with capture: true to catch events before they bubble
    document.addEventListener('click', this.globalClickListener, true);
  }

  private removeGlobalClickListener() {
    if (this.globalClickListener) {
      document.removeEventListener('click', this.globalClickListener, true);
      this.globalClickListener = null;
    }
  }

  onSearchButtonClick() {
    this.hasSearched.set(true);
    this.searchError.set(null); // Clear any previous errors
    
    // Check if this is a trip search pattern
    if (this.handleTripSearch()) {
      return;
    }
    
    // Only trigger search if there's a search value
    if (this.searchValue && this.searchValue.trim().length > 0) {
      // Trigger search with current search value
      this.searchSubject.next(this.searchValue);
    } else {
      // If no search value, clear results and reset loading state
      this.searchResults.set([]);
      this.isLoading.set(false);
      this.totalResultCount.set(0);
    }
  }

  onEnterKeyPress() {
    this.hasSearched.set(true);
    this.searchError.set(null); // Clear any previous errors
    
    // Check if this is a trip search pattern
    if (this.handleTripSearch()) {
      return;
    }
    
    // Only trigger search if there's a search value
    if (this.searchValue && this.searchValue.trim().length > 0) {
      // Trigger search when Enter key is pressed
      this.searchSubject.next(this.searchValue);
    } else {
      // If no search value, clear results and reset loading state
      this.searchResults.set([]);
      this.isLoading.set(false);
      this.totalResultCount.set(0);
    }
  }

  onInputChange(event: any) {
    const query = event.target.value;
    this.searchValue = query;

    // Update navigation suggestions
    const queryLower = query.toLowerCase();
    const filteredSuggestions = this.suggestions().filter(item => item.label.toLowerCase().includes(queryLower));
    this.filteredSuggestions.set(filteredSuggestions);

    // Clear search results when input changes (no auto-search)
    this.searchResults.set([]);
    this.searchError.set(null);
  }

  // Method to be called by parent to close search popover
  closeSearchPopover() {
    if (this.searchPopover && this.searchPopover.hide) {
      this.searchPopover.hide();
    }
    this.isSearchActive = false;
    this.removeBackdrop();
  }

  
  // Handle table filter input
  onFilterInput(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    // Use ViewChild reference to call filterGlobal
    if (this.searchResultsTable && this.searchResultsTable.filterGlobal) {
      this.searchResultsTable.filterGlobal(value, 'contains');
    }
  }

  // Export CSV
  exportCSV() {
    if (this.searchResultsTable && this.searchResults().length > 0) {
      this.exportSearchResultsToCSV();
    }
  }

  private exportSearchResultsToCSV() {
    const data = this.searchResults();
    const columns = this.searchColumns();

    // Create CSV headers
    const headers = columns.map(col => col.header).join(',');

    // Create CSV rows
    const rows = data.map(result => {
      return columns.map(col => {
        let value = this.getFieldValue(result, col.field);

        // Format the value based on column type
        if (value != null) {
          switch (col.type) {
            case 'phone':
              // Remove any formatting for phone numbers
              value = value.toString().replace(/[^\d]/g, '');
              break;
            case 'email':
            case 'text':
            case 'groupNameWithLink':
              // Escape quotes and wrap in quotes if contains comma
              value = value.toString().replace(/"/g, '""');
              if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                value = `"${value}"`;
              }
              break;
            default:
              value = value.toString().replace(/"/g, '""');
              if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                value = `"${value}"`;
              }
          }
        } else {
          value = '';
        }

        return value;
      }).join(',');
    });

    // Combine headers and rows
    const csvContent = [headers, ...rows].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'search-results.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private getFieldValue(data: any, field: string): any {
    if (!data || !field) return null;

    // Handle nested field access (e.g., 'user.name')
    if (field.includes('.')) {
      const fields = field.split('.');
      let value = data;
      for (const f of fields) {
        value = value?.[f];
        if (value == null) return null;
      }
      return value;
    }

    return data[field];
  }

  // Clear table filters
  clearTableFilters() {
    if (this.searchResultsTable) {
      this.searchResultsTable.clear();
    }
  }

  // Clear search input and results
  clearSearch() {
    this.searchValue = '';
    this.searchResults.set([]);
    this.searchError.set(null);
    // Don't reset hasSearched - keep it true so subsequent searches work
    this.hasPerformedSearch.set(false); // Reset the performed search flag
    this.isLoading.set(false);
    this.totalResultCount.set(0);
    
    // Clear navigation suggestions
    this.filteredSuggestions.set(this.suggestions());
    
    // Clear table filters if table exists
    if (this.searchResultsTable) {
      this.searchResultsTable.clear();
    }
    
    // Explicitly clear the input field values
    if (this.searchInput) {
      this.searchInput.nativeElement.value = '';
    }
    if (this.searchInputMobile) {
      this.searchInputMobile.nativeElement.value = '';
    }
    
    // Force change detection to ensure the input field updates
    this.cdr.detectChanges();
    
    // Focus back to the search input
    this.focus();
  }




  // Get skeleton width for different column types
  getSkeletonWidth(field: string): number {
    switch (field) {
      case 'type':
        return 60;
      case 'groupId':
        return 80;
      case 'name':
        return 90;
      case 'company':
        return 85;
      case 'phone1':
      case 'phone2':
        return 70;
      case 'email':
        return 75;
      case 'actions':
        return 50;
      default:
        return 70;
    }
  }


  // Handle trip search pattern: "trip {trip ID}"
  private handleTripSearch(): boolean {
    const query = this.searchValue.trim().toLowerCase();
    
    // Check if the query matches the pattern "trip {number}"
    const tripMatch = query.match(/^trip\s+(\d+)$/);
    
    if (tripMatch) {
      const tripId = parseInt(tripMatch[1], 10);
      
      // Validate that it's a positive number
      if (tripId > 0) {
        this.isLoading.set(true);
        this.searchError.set(null);
        
        // Get the trip details to find the customer ID
        this.tripService.getTrip(tripId).subscribe({
          next: (trip) => {
            // Navigate to the contact card with the customer ID, trip ID, and trip-info fragment
            const contactCardUrl = `/contact-card/${trip.custId}?trip=${tripId}#trip-info`;
            this.router.navigateByUrl(contactCardUrl);
            
            // Close the search popover
            this.closeSearchPopover();
            this.clearSearch();
          },
          error: (error) => {
            this.isLoading.set(false);
            this.searchError.set(`Trip ${tripId} not found. Please check the trip ID and try again.`);
          }
        });
        
        return true; // Indicate that we handled the trip search
      }
    }
    
    return false; // Not a trip search pattern
  }


  // Open log activity datapad
  openLogActivity(result: SearchResult) {
    const inputData: EditNoteComponentInputs = {
      custId: result.groupId,
      customerName: result.company,
      tripDetails: [],
      contacts: []
    };

    // Close search popover when opening datapad
    this.closeSearchPopover();

    this.dataPadService.showPanel<EditNoteComponentInputs>({
      panelName: 'editNote',
      inputData: inputData
    });
  }

  // Open schedule activity datapad
  openScheduleActivity(result: SearchResult) {
    const inputData: EditScheduledActivityComponentInputs = {
      custId: result.groupId,
      customerName: result.company,
      tripDetails: [],
      contacts: []
    };

    // Close search popover when opening datapad
    this.closeSearchPopover();

    this.dataPadService.showPanel<EditScheduledActivityComponentInputs>({
      panelName: 'editScheduledActivity',
      inputData: inputData
    });
  }

}
